import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import AuthLayout from '../components/AuthLayout';
import serviceRequestService, { ServiceRequestType, KanbanData } from '../services/serviceRequest.service';
import userService from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';

const priorityColors = {
  low: '#8bc34a',
  medium: '#ffc107',
  high: '#ff9800',
  critical: '#f44336'
};

const priorityLabels = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica'
};

const typeLabels = {
  maintenance: 'Manutenção',
  installation: 'Instalação',
  inspection: 'Inspeção',
  repair: 'Reparo',
  other: 'Outro'
};

const ServiceRequestKanban: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [kanbanData, setKanbanData] = useState<KanbanData>({
    waiting: [],
    in_progress: [],
    completed: []
  });
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [selectedTechnician, setSelectedTechnician] = useState<string>('');
  
  useEffect(() => {
    loadKanbanData();
    loadTechnicians();
  }, []);
  
  const loadKanbanData = async (techId?: string) => {
    setLoading(true);
    try {
      const data = await serviceRequestService.getServiceRequestsByStatus(techId || selectedTechnician);
      setKanbanData(data);
    } catch (error) {
      console.error('Error loading kanban data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTechnicians = async () => {
    try {
      const technicians = await userService.getAvailableTechnicians();
      setTechnicians(Array.isArray(technicians) ? technicians : []);
    } catch (error) {
      console.error('Error loading technicians:', error);
      // Continue even if technicians can't be loaded
      setTechnicians([]);
    }
  };
  
  const handleDragEnd = async (result: any) => {
    const { source, destination, draggableId } = result;
    
    // Dropped outside a droppable area
    if (!destination) return;
    
    // Dropped in the same place
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) return;
    
    // Map droppableId to status
    const statusMap: Record<string, 'waiting' | 'in_progress' | 'completed'> = {
      waiting: 'waiting',
      in_progress: 'in_progress',
      completed: 'completed'
    };
    
    const newStatus = statusMap[destination.droppableId];
    
    if (newStatus) {
      // Optimistically update UI
      const sourceList = [...kanbanData[source.droppableId as keyof KanbanData] as ServiceRequestType[]];
      const destList = source.droppableId === destination.droppableId
        ? sourceList
        : [...kanbanData[destination.droppableId as keyof KanbanData] as ServiceRequestType[]];
      
      const [removed] = sourceList.splice(source.index, 1);
      destList.splice(destination.index, 0, { ...removed, status: newStatus });
      
      const newData = {
        ...kanbanData,
        [source.droppableId]: sourceList,
        [destination.droppableId]: destList
      };
      
      setKanbanData(newData as KanbanData);
      
      // Update in the backend
      try {
        await serviceRequestService.updateServiceRequest(draggableId, { status: newStatus });
      } catch (error) {
        console.error('Error updating service request status:', error);
        // Revert on error
        loadKanbanData();
      }
    }
  };
  
  const handleFilterChange = (techId: string) => {
    setSelectedTechnician(techId);
    loadKanbanData(techId);
    setFilterDialogOpen(false);
  };
  
  const getCardStyle = (isDragging: boolean, priority: string) => ({
    userSelect: 'none',
    margin: '0 0 8px 0',
    backgroundColor: isDragging ? theme.palette.background.paper : theme.palette.background.default,
    boxShadow: isDragging ? theme.shadows[8] : theme.shadows[1],
    borderLeft: `4px solid ${priorityColors[priority as keyof typeof priorityColors] || theme.palette.primary.main}`,
  });
  
  const getColumnStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? theme.palette.action.hover : theme.palette.background.paper,
    padding: theme.spacing(1),
    width: '100%',
    minHeight: '300px',
    maxHeight: 'calc(100vh - 240px)',
    overflowY: 'auto' as const
  });
  
  const renderServiceRequestCard = (serviceRequest: ServiceRequestType, index: number) => (
    <Draggable
      key={serviceRequest._id}
      draggableId={serviceRequest._id || ''}
      index={index}
    >
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          sx={getCardStyle(snapshot.isDragging, serviceRequest.priority)}
          onClick={() => navigate(`/service-requests/${serviceRequest._id}`)}
        >
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {serviceRequest.number}
              </Typography>
              <Chip
                size="small"
                label={priorityLabels[serviceRequest.priority as keyof typeof priorityLabels]}
                sx={{
                  backgroundColor: priorityColors[serviceRequest.priority as keyof typeof priorityColors],
                  color: '#fff'
                }}
              />
            </Box>
            
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {serviceRequest.description.length > 60
                ? `${serviceRequest.description.substring(0, 60)}...`
                : serviceRequest.description}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AssignmentIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {typeLabels[serviceRequest.type as keyof typeof typeLabels]}
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Cliente: {serviceRequest.client && typeof serviceRequest.client === 'object' 
                ? serviceRequest.client.name 
                : 'Carregando...'}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Ativo: {serviceRequest.asset && typeof serviceRequest.asset === 'object' 
                ? serviceRequest.asset.name 
                : 'Carregando...'}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              {serviceRequest.assignedTo ? (
                <Tooltip title={typeof serviceRequest.assignedTo === 'object' ? serviceRequest.assignedTo.displayName : 'Técnico'}>
                  <Avatar
                    src={typeof serviceRequest.assignedTo === 'object' ? serviceRequest.assignedTo.photoURL : undefined}
                    sx={{ width: 24, height: 24 }}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>
                </Tooltip>
              ) : (
                <Chip size="small" label="Não atribuído" variant="outlined" />
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {serviceRequest.createdAt 
                    ? format(new Date(serviceRequest.createdAt), 'dd/MM/yyyy', { locale: ptBR })
                    : 'Data desconhecida'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Draggable>
  );
  
  return (
    <AuthLayout title="Painel de Chamados Técnicos">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Painel de Chamados Técnicos
          </Typography>
          
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/service-requests/new')}
              sx={{ mr: 1 }}
            >
              Novo Chamado
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFilterDialogOpen(true)}
              sx={{ mr: 1 }}
            >
              Filtrar
            </Button>
            
            <IconButton onClick={() => loadKanbanData()}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: '#f44336', 
                      mr: 1 
                    }} />
                    Aguardando
                    <Chip 
                      label={kanbanData.waiting.length} 
                      size="small" 
                      sx={{ ml: 1 }} 
                    />
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Droppable droppableId="waiting">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={getColumnStyle(snapshot.isDraggingOver)}
                      >
                        {kanbanData.waiting.map((item, index) => renderServiceRequestCard(item, index))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: '#ff9800', 
                      mr: 1 
                    }} />
                    Em Execução
                    <Chip 
                      label={kanbanData.in_progress.length} 
                      size="small" 
                      sx={{ ml: 1 }} 
                    />
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Droppable droppableId="in_progress">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={getColumnStyle(snapshot.isDraggingOver)}
                      >
                        {kanbanData.in_progress.map((item, index) => renderServiceRequestCard(item, index))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: '#4caf50', 
                      mr: 1 
                    }} />
                    Finalizado
                    <Chip 
                      label={kanbanData.completed.length} 
                      size="small" 
                      sx={{ ml: 1 }} 
                    />
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Droppable droppableId="completed">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={getColumnStyle(snapshot.isDraggingOver)}
                      >
                        {kanbanData.completed.map((item, index) => renderServiceRequestCard(item, index))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Paper>
              </Grid>
            </Grid>
          </DragDropContext>
        )}
      </Box>
      
      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)}>
        <DialogTitle>Filtrar Chamados</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="technician-select-label">Técnico</InputLabel>
            <Select
              labelId="technician-select-label"
              value={selectedTechnician}
              label="Técnico"
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              <MenuItem value="">Todos os Técnicos</MenuItem>
              {technicians.map((tech) => (
                <MenuItem key={tech._id} value={tech._id}>
                  {tech.displayName}
                </MenuItem>
              ))}
              {currentUser && (
                <MenuItem value={currentUser.uid}>Meus Chamados</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
};

export default ServiceRequestKanban;
